/**
 * Referral ladder (Morning Brew style). The single source of truth for the visible, tiered
 * invite reward in the web app. Every tier is derived from the reader's lifetime
 * rewarded-referral count, so nothing extra is persisted anywhere.
 *
 * Keep these thresholds and names in sync with the worker copy in worker/src/cards.ts
 * (referralLadder + REFERRAL_TIERS).
 */

export interface ReferralTier {
  /** Rewarded referrals needed to unlock this tier. */
  threshold: number;
  /** Short badge name shown in the ladder. */
  name: string;
  /** Plain-language reward, shown as a tooltip / caption. */
  reward: string;
}

/** The four ladder tiers, ascending. Tier 1 is the freeze token already granted on sign-in. */
export const REFERRAL_TIERS: ReferralTier[] = [
  { threshold: 1, name: "Streak Freeze", reward: "+1 streak freeze" },
  { threshold: 3, name: "Curious Mind", reward: "Curious Mind badge" },
  { threshold: 5, name: "Founding Reader", reward: "Founding Reader badge" },
  { threshold: 10, name: "Ambassador", reward: "Ambassador badge" },
];

export interface LadderTierView extends ReferralTier {
  /** True once the reader's count has reached this tier's threshold. */
  reached: boolean;
}

export interface ReferralLadder {
  /** Sanitised rewarded-referral count (never negative, whole number). */
  count: number;
  /** Name of the highest tier reached, or null before the first. */
  currentTier: string | null;
  /** Next tier to unlock, plus how many more referrals it needs. Null once maxed out. */
  nextTier: { name: string; threshold: number; needed: number } | null;
  /** 0..100 progress across the CURRENT segment toward the next tier (100 when maxed). */
  progressPct: number;
  /** Every tier with a reached flag, for rendering the locked / unlocked badge row. */
  tiers: LadderTierView[];
}

/**
 * Compute the full ladder view for a rewarded-referral count. Pure and null-safe: any
 * non-finite / negative input is treated as 0 so the UI never breaks.
 */
export function computeReferralLadder(rawCount: number): ReferralLadder {
  const count = Number.isFinite(rawCount) ? Math.max(0, Math.floor(rawCount)) : 0;

  let currentTier: string | null = null;
  let next: ReferralTier | null = null;
  for (const tier of REFERRAL_TIERS) {
    if (count >= tier.threshold) {
      currentTier = tier.name;
    } else if (!next) {
      next = tier;
    }
  }

  const nextTier = next
    ? { name: next.name, threshold: next.threshold, needed: Math.max(1, next.threshold - count) }
    : null;

  // Fill the bar across the current segment (last reached threshold -> next threshold) so the
  // progress reads as "distance to the next badge", not a fraction of the whole ladder.
  const reached = REFERRAL_TIERS.filter((t) => count >= t.threshold).map((t) => t.threshold);
  const prevThreshold = reached.length ? Math.max(...reached) : 0;
  const progressPct = next
    ? Math.min(100, Math.max(0, Math.round(((count - prevThreshold) / (next.threshold - prevThreshold)) * 100)))
    : 100;

  const tiers: LadderTierView[] = REFERRAL_TIERS.map((t) => ({ ...t, reached: count >= t.threshold }));

  return { count, currentTier, nextTier, progressPct, tiers };
}
